window.ttx = window.ttx || {};

ttx.viewmodels = {};
ttx.viewmodels.register = function (modelType, func) {
    ttx.viewmodels[modelType] = func(modelType);
};

// ============================================================================
// MDF 标准 ViewModel 能力
//
// 保留上方 register 注册器不动，下方补充 MDF 标准生命周期事件、API 方法与 biz 对象。
// ViewModel 继承 ContainerModel（ContainerModel 通过 tx.models.js 异步加载，
// 故采用延迟注册：轮询等待 ContainerModel 就绪后再建立原型链）。
//
// ## 生命周期事件（按执行顺序，通过 on/execute 触发）
// | 事件 | 说明 |
// |------|------|
// | afterMount      | 页面容器渲染完成，在 afterLoadMeta 之前调用，无形参 |
// | afterLoadMeta   | 单据 UI 元数据已返回；异步事件，可返回 promise |
// | afterInit       | 页面初始化完成 |
// | beforeSetState  | 状态设置前；返回 false 中止 |
// | afterSetState   | 状态设置后 |
// | afterLoadData   | 单据业务数据已返回且已渲染，同步事件 |
// | afterRule       | 动作规则执行完 |
//
// ## 其它过程事件
// | 事件 | 说明 |
// |------|------|
// | beforeSetMode          | 页面状态改变前，args.oldValue/newValue |
// | modeChange             | 仅卡片页（edit/browser/add） |
// | beforeChangeMasterOrg  | 主组织切换前 |
// | afterMasterOrgChange   | 主组织切换后 |
// | beforeValidate         | 返回 false 阻止后续校验 |
// | beforeTreeSelect       | 树节点选择前置 |
// | return                 | 页面变更事件，data.data.action：activeTab/isRefresh/isCloseAction/isReOpen |
// ============================================================================

// MDF 通信命名空间（单数）
ttx.viewmodel = ttx.viewmodel || {};

/**
 * biz 对象 — 按钮前后置事件
 *
 * 按钮前后置事件规则：action 前加 before 或 after，action 首字母大写。
 * 如新增 add → beforeAdd / afterAdd。
 *
 * @example
 * // 触发 beforeAdd → add → afterAdd
 * viewmodel.biz.do('add', viewmodel, { id: 1 });
 */
ttx.viewmodel.biz = {
    do: function (actionName, viewmodel, params) {
        if (!actionName || !viewmodel) return false;
        // action 首字母大写，拼接 before/after 事件名
        var capName = actionName.charAt(0).toUpperCase() + actionName.slice(1);
        var beforeEvent = 'before' + capName;
        var afterEvent = 'after' + capName;
        // before 返回 false 中止
        if (viewmodel.execute(beforeEvent, params) === false) return false;
        // 执行 action（若 viewmodel 上存在同名方法则调用）
        var result;
        if (typeof viewmodel[actionName] === 'function') {
            result = viewmodel[actionName](params);
        }
        viewmodel.execute(afterEvent, params);
        return result !== false;
    }
};

/**
 * 延迟注册 ViewModel 类
 * 依赖 ContainerModel（异步加载），就绪后建立原型链并注册到 ttx.viewmodels / ttx.models
 */
(function _registerViewModel() {
    // 等待 ContainerModel 与 getPrototype 就绪
    if (!(ttx.models && ttx.models.ContainerModel && ttx.utils && ttx.utils.getPrototype)) {
        setTimeout(_registerViewModel, 50);
        return;
    }
    // 已注册则跳过
    if (ttx.viewmodels.ViewModel) return;

    var viewModel = function (data) {
        ttx.models.ContainerModel.call(this, data);
    };
    viewModel.prototype = ttx.utils.getPrototype(ttx.models.ContainerModel.prototype);
    viewModel.prototype.modelType = 'ViewModel';

    // ===== 生命周期触发方法 =====

    /** afterMount — 页面容器渲染完成，在 afterLoadMeta 之前调用，无形参 */
    viewModel.prototype.afterMount = function () {
        this.execute('afterMount');
    };

    /** afterLoadMeta — 单据 UI 元数据已返回；异步事件，可返回 promise */
    viewModel.prototype.afterLoadMeta = function () {
        return this.execute('afterLoadMeta');
    };

    /** afterInit — 页面初始化完成 */
    viewModel.prototype.afterInit = function () {
        this.execute('afterInit');
    };

    /** afterLoadData — 单据业务数据已返回且已渲染，同步事件 */
    viewModel.prototype.afterLoadData = function () {
        this.execute('afterLoadData');
    };

    /** afterRule — 动作规则执行完 */
    viewModel.prototype.afterRule = function () {
        this.execute('afterRule');
    };

    // ===== 状态设置（beforeSetState / afterSetState）=====

    /**
     * 设置状态（覆盖父类，触发 beforeSetState / afterSetState 事件）
     * beforeSetState 返回 false 中止
     */
    viewModel.prototype.setState = function (name, value, ctrlName) {
        var args = { name: name, value: value, ctrlName: ctrlName, oldValue: this._get_data(name) };
        if (this.execute('beforeSetState', args) === false) return;
        // 调用父类 ContainerModel.setState
        ttx.models.ContainerModel.prototype.setState.call(this, name, value, ctrlName);
        this.execute('afterSetState', args);
    };

    // ===== 页面模式（beforeSetMode / modeChange）=====

    /**
     * 设置页面模式（卡片页：edit/browser/add）
     * 触发 beforeSetMode / modeChange 事件；before 返回 false 中止
     */
    viewModel.prototype.setMode = function (mode) {
        var oldValue = this._get_data('currentMode');
        var args = { oldValue: oldValue, newValue: mode };
        if (this.execute('beforeSetMode', args) === false) return;
        this._set_data('currentMode', mode, true);
        this._set_data('pageMode', mode, true);
        // modeChange：仅卡片页触发
        this.execute('modeChange', mode);
        this.doPropertyChange('setState', { currentMode: mode });
    };

    /** 获取当前页面模式 */
    viewModel.prototype.getMode = function () {
        return this._get_data('currentMode');
    };

    // ===== 主组织切换（beforeChangeMasterOrg / afterMasterOrgChange）=====

    /**
     * 切换主组织
     * 触发 beforeChangeMasterOrg / afterMasterOrgChange 事件；before 返回 false 中止
     */
    viewModel.prototype.setMasterOrg = function (orgId) {
        if (this.execute('beforeChangeMasterOrg', { orgId: orgId }) === false) return;
        this._set_data('masterOrgId', orgId, true);
        this.execute('afterMasterOrgChange', { orgId: orgId });
    };

    // ===== 校验（beforeValidate）=====

    /**
     * 表单校验（覆盖父类，触发 beforeValidate 事件）
     * beforeValidate 返回 false 阻止后续校验
     */
    viewModel.prototype.validate = function () {
        if (this.execute('beforeValidate') === false) return false;
        return ttx.models.ContainerModel.prototype.validate.call(this);
    };

    // ===== 树节点选择（beforeTreeSelect）=====

    /**
     * 树节点选择前置
     * 触发 beforeTreeSelect 事件；返回 false 中止
     */
    viewModel.prototype.beforeTreeSelect = function (keys) {
        if (this.execute('beforeTreeSelect', { keys: keys }) === false) return false;
        return true;
    };

    // ===== API 方法 =====

    // 注：getDomainKey / getViewMeta / getSubPageViewModels / getSubPageViewModel / getParams
    //     均继承自 ContainerModel，此处不重复定义。

    /**
     * 获取真实的 serviceCode
     * @returns {string} 真实服务编码
     */
    viewModel.prototype.getRealServiceCode = function () {
        var params = (this.getParams && this.getParams()) || {};
        return this._get_data('serviceCode') || params.serviceCode || params.busiObj || '';
    };

    /**
     * 设置页面参数
     * @param {Object} params 页面参数（mode/billType/billNo/billData/carryParams 等）
     */
    viewModel.prototype.setParams = function (params) {
        if (!params || typeof params !== 'object') return;
        var oldParams = this._get_data('params') || {};
        this._set_data('params', ttx.utils.extend({}, oldParams, params), true);
    };

    /**
     * MDF 通信 API
     * @param {Object} options 通信选项
     * @param {string} options.type 通信类型：'return' / 'modal' / 'platform'
     * @param {Object} options.payload 通信数据
     *
     * - return：页面变更事件，触发 'return' 事件，payload.action 可为
     *   activeTab / isRefresh / isCloseAction / isReOpen
     * - modal：模态框通信，调用 ttx.modal
     * - platform：平台通信，触发 'communication' 事件
     */
    viewModel.prototype.communication = function (options) {
        if (!options || !options.type) return;
        var type = options.type;
        var payload = options.payload || {};
        switch (type) {
            case 'return':
                // 页面变更事件
                this.execute('return', { data: { action: payload.action, data: payload } });
                break;
            case 'modal':
                // 模态框通信
                if (ttx.modal && typeof ttx.modal.open === 'function') {
                    ttx.modal.open(payload);
                }
                break;
            case 'platform':
                // 平台通信
                this.execute('communication', { type: type, payload: payload });
                break;
            default:
                ttx.console.warn('[ViewModel.communication]: 未知通信类型 ' + type);
        }
    };

    // 注册到 viewmodels 与 models 命名空间
    ttx.viewmodels.ViewModel = viewModel;
    ttx.models.ViewModel = viewModel;
})();
